import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle, XCircle, Clock, User, FileText, Shield } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

export const ReferralDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { get, put } = useApi();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [referral, setReferral] = useState(null);
    const [responding, setResponding] = useState(false);
    const [responseNotes, setResponseNotes] = useState('');

    useEffect(() => {
        fetchReferral();
    }, [id]);

    const fetchReferral = async () => {
        try {
            setLoading(true);
            const response = await get(`/referrals/${id}`);
            setReferral(response.data.data);
        } catch (err) {
            setError('Failed to load referral details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCounselorResponse = async (accepted) => {
        try {
            setResponding(true);
            await put(`/referrals/${id}/counselor-response`, {
                accepted,
                notes: responseNotes
            });
            fetchReferral();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit response');
        } finally {
            setResponding(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel this referral?')) return;

        try {
            await put(`/referrals/${id}/cancel`, {
                reason: 'Cancelled by counselor'
            });
            navigate(`/${user.role}/referrals`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel referral');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error || !referral) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <p className="ml-3 text-sm text-red-700">{error || 'Referral not found'}</p>
                    </div>
                </div>
            </div>
        );
    }

    const canRespond = user.role === 'counselor' &&
        referral.targetCounselor._id === user._id &&
        referral.status === 'student_approved';

    const canCancel = user.role === 'counselor' &&
        referral.referringCounselor._id === user._id &&
        !['counselor_accepted', 'completed', 'cancelled'].includes(referral.status);

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
            </button>

            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200">
                {/* Status Header */}
                <div className="bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 text-white px-6 py-4 rounded-t-lg">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl font-bold">Referral Details</h1>
                            <p className="text-blue-100 text-sm mt-1">
                                Created {new Date(referral.createdAt).toLocaleDateString()}
                            </p>
                        </div>
                        <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">
                            {referral.status.replace(/_/g, ' ').toUpperCase()}
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Participants */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-xs text-gray-600 mb-2 flex items-center">
                                <User className="h-4 w-4 mr-1" />
                                Student
                            </p>
                            <p className="font-medium text-gray-900">{referral.client?.fullName}</p>
                            <p className="text-sm text-gray-600">{referral.client?.email}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-xs text-gray-600 mb-2">Referring Counselor</p>
                            <p className="font-medium text-gray-900">{referral.referringCounselor?.fullName}</p>
                            <p className="text-sm text-gray-600">{referral.referringCounselor?.email}</p>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                            <p className="text-xs text-gray-600 mb-2">Target Counselor</p>
                            <p className="font-medium text-gray-900">{referral.targetCounselor?.fullName}</p>
                            <p className="text-sm text-gray-600">{referral.targetCounselor?.email}</p>
                            {referral.targetCounselor?.staffInfo?.specializations && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {referral.targetCounselor.staffInfo.specializations.map((spec, idx) => (
                                        <span key={idx} className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                                            {spec.replace(/_/g, ' ')}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-gray-600" />
                            Reason for Referral
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-700">{referral.reason}</p>
                        </div>
                    </div>

                    {/* Partial Notes */}
                    {(referral.partialNotes?.presentingIssues || referral.partialNotes?.recommendedFocus || referral.partialNotes?.safetyConsiderations) && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <Shield className="h-5 w-5 mr-2 text-gray-600" />
                                Shared Information
                            </h3>
                            <div className="space-y-3">
                                {referral.partialNotes.presentingIssues && (
                                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                                        <p className="text-xs font-medium text-gray-600 mb-1">Presenting Issues</p>
                                        <p className="text-gray-800">{referral.partialNotes.presentingIssues}</p>
                                    </div>
                                )}

                                {referral.partialNotes.recommendedFocus && (
                                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                                        <p className="text-xs font-medium text-gray-600 mb-1">Recommended Focus</p>
                                        <p className="text-gray-800">{referral.partialNotes.recommendedFocus}</p>
                                    </div>
                                )}

                                {referral.partialNotes.safetyConsiderations && (
                                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                        <p className="text-xs font-medium text-gray-600 mb-1">Safety Considerations</p>
                                        <p className="text-gray-800">{referral.partialNotes.safetyConsiderations}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Counselor Response Section */}
                    {canRespond && (
                        <div className="border-t pt-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Your Response</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Notes (Optional)
                                    </label>
                                    <textarea
                                        value={responseNotes}
                                        onChange={(e) => setResponseNotes(e.target.value)}
                                        rows={3}
                                        maxLength={300}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        placeholder="Add any notes about accepting or declining this referral..."
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{responseNotes.length}/300</p>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => handleCounselorResponse(false)}
                                        disabled={responding}
                                        className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:bg-red-300 flex items-center justify-center gap-2"
                                    >
                                        <XCircle className="h-5 w-5" />
                                        {responding ? 'Processing...' : 'Decline Referral'}
                                    </button>
                                    <button
                                        onClick={() => handleCounselorResponse(true)}
                                        disabled={responding}
                                        className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-green-300 flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle className="h-5 w-5" />
                                        {responding ? 'Processing...' : 'Accept Referral'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Response Info (if already responded) */}
                    {referral.counselorResponse?.respondedAt && (
                        <div className={`border-t pt-6 ${referral.counselorResponse.accepted ? 'bg-green-50' : 'bg-red-50'} p-4 rounded-lg`}>
                            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                                {referral.counselorResponse.accepted ? (
                                    <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                                ) : (
                                    <XCircle className="h-5 w-5 mr-2 text-red-600" />
                                )}
                                Counselor Response
                            </h3>
                            <p className="text-sm text-gray-700 mb-2">
                                {referral.counselorResponse.accepted ? 'Accepted' : 'Declined'} on{' '}
                                {new Date(referral.counselorResponse.respondedAt).toLocaleDateString()}
                            </p>
                            {referral.counselorResponse.notes && (
                                <p className="text-gray-800 mt-2">{referral.counselorResponse.notes}</p>
                            )}
                        </div>
                    )}

                    {/* Cancel Button */}
                    {canCancel && (
                        <div className="border-t pt-6">
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium"
                            >
                                Cancel Referral
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferralDetails;
